import {
    computed,
    inject,
    Injectable,
    signal,
    Signal,
    WritableSignal,
} from '@angular/core';
import { DatiqAppConfig } from '@dat/datiq-core/models';
import {
    Client,
    OptimizelyDecideOption,
    OptimizelyDecision,
    OptimizelyUserContext,
} from '@optimizely/optimizely-sdk';
import { noop, Observable, of } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { IqTrendlinesOptimizelyInitializerService } from './iq-trendlines-optimizely-initializer.service';

@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesOptimizelyService {
    private _client: Client;
    private _user: OptimizelyUserContext;
    private _init: IqTrendlinesOptimizelyInitializerService = inject(
        IqTrendlinesOptimizelyInitializerService,
    );
    private _appConfig: DatiqAppConfig = inject(DatiqAppConfig);
    private intialized: WritableSignal<boolean> = signal(false);
    private optimizelyLoaded: WritableSignal<boolean> = signal(false);

    canEvaluateFlags: Signal<boolean> = computed(() => {
        return this.intialized() && this.optimizelyLoaded();
    });
    constructor() {}

    init(): Observable<Client> {
        if (this.intialized()) {
            return of(this._client);
        }
        this.intialized.set(true);
        return this.setupUser$(this._appConfig);
    }

    getClient(): Client {
        return this._client;
    }

    setClient(newClient: Client): void {
        this._client = newClient;
    }

    getUser(): OptimizelyUserContext {
        return this._user;
    }

    getVisitorId(): string {
        const existingId = localStorage.getItem('optimizely_visitor_id');
        if (existingId) {
            return existingId;
        }

        const newId = Math.random().toString(36).substring(2, 15);
        localStorage.setItem('optimizely_visitor_id', newId);
        return newId;
    }

    setUser(client: Client) {
        const userId = this.getVisitorId();

        this._user = client.createUserContext(userId, {});

        !this._user
            ? console.error('Error in setting user for Optimizely')
            : noop();

        return this._user;
    }

    setupUser$(config: DatiqAppConfig): Observable<Client> {
        return this._init.initialize(config).pipe(
            tap((client: Client) =>
                !!client ? this.setClient(client) : noop(),
            ),
            filter((client) => !!client),
            tap((client) => {
                this.setUser(client);
                this.optimizelyLoaded.set(true);
            }),
            take(1),
        );
    }

    getFeatureFlag(flagName: string): Signal<OptimizelyDecision | null> {
        return computed(() => {
            if (!this.canEvaluateFlags()) return null;

            const decision: OptimizelyDecision =
                this.getUser()?.decide(flagName);
            if (!decision) {
                console.error(`No decision found for flag: ${flagName}`);
                return null;
            }
            return decision;
        });
    }

    isFlagEnabled(flagKey: string): Signal<boolean> {
        return computed(() => {
            if (!this.canEvaluateFlags()) return false;
            const user = this.getUser();
            return user?.decide(flagKey, [
                OptimizelyDecideOption.DISABLE_DECISION_EVENT,
            ])?.enabled;
        });
    }

    hasVariation(flagKey: string, variation: string): Signal<boolean> {
        return computed(() => {
            if (!this.canEvaluateFlags()) return false;
            const user = this.getUser();
            return user?.decide(flagKey)?.variationKey === variation;
        });
    }

    enrollExperimentFlag(flagName: string): void {
        const decision: OptimizelyDecision = this.getUser()?.decide(flagName);
    }
}
